import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface EntityTypeVariation {
  id: string;
  name: string;
  singular: string;
  plural: string;
  variations: string[];
}

interface EntityTypeVariationsFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function EntityTypeVariationsForm({ initialContent, onSave, onCancel }: EntityTypeVariationsFormProps) {
  const { t } = useTranslation();
  const [entityTypes, setEntityTypes] = useState<EntityTypeVariation[]>([]);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [newVariation, setNewVariation] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as {
          entity_type_variations: Record<string, { singular: string; plural: string; variations: string[] }>;
        };

        if (parsed?.entity_type_variations) {
          const types = Object.entries(parsed.entity_type_variations).map(([name, data], index) => ({
            id: `type-${Date.now()}-${index}`,
            name,
            singular: data.singular || '',
            plural: data.plural || '',
            variations: data.variations || [],
          }));
          setEntityTypes(types);
        }
      } catch (error) {
        console.error('Error parsing entity type variations YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddEntityType = () => {
    const newType: EntityTypeVariation = {
      id: `type-${Date.now()}`,
      name: '',
      singular: '',
      plural: '',
      variations: [],
    };
    setEntityTypes([newType, ...entityTypes]);
    setExpandedTypes(new Set([...expandedTypes, newType.id]));
  };

  const handleUpdateEntityType = (id: string, field: keyof Omit<EntityTypeVariation, 'id' | 'variations'>, value: string) => {
    setEntityTypes(entityTypes.map(type =>
      type.id === id ? { ...type, [field]: value } : type
    ));
  };

  const handleDeleteEntityType = (id: string) => {
    setEntityTypes(entityTypes.filter(type => type.id !== id));
    const newExpanded = new Set(expandedTypes);
    newExpanded.delete(id);
    setExpandedTypes(newExpanded);
  };

  const handleAddVariation = (typeId: string) => {
    const variationToAdd = newVariation[typeId]?.trim();
    if (!variationToAdd) return;

    setEntityTypes(entityTypes.map(type =>
      type.id === typeId
        ? { ...type, variations: [...type.variations, variationToAdd] }
        : type
    ));
    setNewVariation({ ...newVariation, [typeId]: '' });
  };

  const handleDeleteVariation = (typeId: string, variationIndex: number) => {
    setEntityTypes(entityTypes.map(type =>
      type.id === typeId
        ? { ...type, variations: type.variations.filter((_, i) => i !== variationIndex) }
        : type
    ));
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTypes(newExpanded);
  };

  const handleSave = () => {
    // Convert to object format
    const variationsObj: Record<string, { singular: string; plural: string; variations: string[] }> = {};
    entityTypes.forEach(type => {
      if (type.name) {
        variationsObj[type.name] = {
          singular: type.singular,
          plural: type.plural,
          variations: type.variations,
        };
      }
    });

    const yamlContent = yaml.dump(
      { entity_type_variations: variationsObj },
      {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
      }
    );

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <p className="text-sm text-muted-foreground">
            {t('admin.configForms.entityTypeVariations.intro')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('admin.configForms.entityTypeVariations.hint')}
          </p>
        </div>
        <Button
          onClick={handleAddEntityType}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('admin.configForms.entityTypeVariations.addType')}
        </Button>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {entityTypes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('admin.configForms.entityTypeVariations.empty')}
          </div>
        ) : (
          entityTypes.map((type) => (
            <div
              key={type.id}
              className="border border-border rounded-lg overflow-hidden bg-card"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                onClick={() => toggleExpanded(type.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {type.name || 'NewEntityType'}
                    </code>
                    {type.singular && (
                      <span className="text-sm text-muted-foreground">
                        {type.singular}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t('admin.configForms.entityTypeVariations.variationCount', { count: type.variations.length })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteEntityType(type.id);
                    }}
                    className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {expandedTypes.has(type.id) ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {expandedTypes.has(type.id) && (
                <div className="p-4 pt-0 space-y-4 border-t border-border">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('admin.configForms.entityTypeVariations.typeName')}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={type.name}
                      onChange={(e) => handleUpdateEntityType(type.id, 'name', e.target.value)}
                      placeholder={t('admin.configForms.entityTypeVariations.typeNamePlaceholder')}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('admin.configForms.entityTypeVariations.pascalCaseHint')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.entityTypeVariations.singular')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={type.singular}
                        onChange={(e) => handleUpdateEntityType(type.id, 'singular', e.target.value)}
                        placeholder={t('admin.configForms.entityTypeVariations.singularPlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.entityTypeVariations.plural')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={type.plural}
                        onChange={(e) => handleUpdateEntityType(type.id, 'plural', e.target.value)}
                        placeholder={t('admin.configForms.entityTypeVariations.pluralPlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('admin.configForms.entityTypeVariations.variations')}
                    </label>
                    <div className="space-y-2">
                      {type.variations.map((variation, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="flex-1 px-3 py-2 bg-muted rounded-md text-sm">
                            {variation}
                          </span>
                          <button
                            onClick={() => handleDeleteVariation(type.id, index)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newVariation[type.id] || ''}
                          onChange={(e) => setNewVariation({ ...newVariation, [type.id]: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddVariation(type.id)}
                          placeholder={t('admin.configForms.entityTypeVariations.variationPlaceholder')}
                          className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm"
                        />
                        <Button
                          onClick={() => handleAddVariation(type.id)}
                          size="sm"
                          variant="outline"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
